import { realpath } from "node:fs/promises";
import { resolve } from "node:path";

const fileMutationQueues = new Map<string, Promise<void>>();
let registrationQueue = Promise.resolve();

function isMissingPathError(error: unknown): boolean {
	return (
		typeof error === "object" &&
		error !== null &&
		"code" in error &&
		(error.code === "ENOENT" || error.code === "ENOTDIR")
	);
}

/**
 * Canonical key for a mutation target: absolute, and symlink-resolved when the path
 * exists so an alias and its target share one queue. A not-yet-created file falls back to
 * the resolved path, which is what lets `write` create files.
 *
 * Exported so callers that need to name the same target (diagnostics, coordination) cannot
 * drift into a second normalizer.
 */
export async function canonicalMutationKey(filePath: string): Promise<string> {
	const resolvedPath = resolve(filePath);
	try {
		return await realpath(resolvedPath);
	} catch (error) {
		if (isMissingPathError(error)) {
			return resolvedPath;
		}
		throw error;
	}
}

/**
 * Serialize file mutation operations targeting the same file.
 * Operations for different files still run in parallel.
 *
 * The holder is handed the canonical key this call registered under. Registration already
 * paid for the `realpath`, so a holder that has to name its own target in a diagnostic takes
 * the answer from here instead of resolving the path a second time under the lock.
 */
export async function withFileMutationQueue<T>(filePath: string, fn: (canonicalKey: string) => Promise<T>): Promise<T> {
	const registration = registrationQueue.then(async () => {
		const key = await canonicalMutationKey(filePath);
		const currentQueue = fileMutationQueues.get(key) ?? Promise.resolve();

		let releaseNext!: () => void;
		const nextQueue = new Promise<void>((resolveQueue) => {
			releaseNext = resolveQueue;
		});
		const chainedQueue = currentQueue.then(() => nextQueue);
		fileMutationQueues.set(key, chainedQueue);

		return { key, currentQueue, chainedQueue, releaseNext };
	});
	registrationQueue = registration.then(
		() => undefined,
		() => undefined,
	);

	const { key, currentQueue, chainedQueue, releaseNext } = await registration;
	await currentQueue;
	try {
		return await fn(key);
	} finally {
		releaseNext();
		if (fileMutationQueues.get(key) === chainedQueue) {
			fileMutationQueues.delete(key);
		}
	}
}
